import { Collapse } from '@components/Collapse';
import { Dropdown, type DropdownItem } from '@components/Dropdown';
import Tabbar from '@components/Tabbar';
import { customButtonDefault } from '@modules/edit';
import {
  type CFSetData,
  CustomFeatureProvider,
} from '@modules/panel/CustomFeatureContext';
import FeatureSwitch from '@modules/panel/components/FeatureSwitch';
import CSceneSetting from '@modules/panel/customFeatureComponents/CSceneSetting';
import { $editingMapMarkerApi } from '@stores/editingMarkerStores';
import {
  type CustomFeature,
  FeatureType,
  type STMapMarkerFeature,
  STMapMarkerType,
} from '@types';
import cn from '@utils/cn';
import { useState } from 'react';
import { isMapFeatureMarker } from '../utils/markerTypeCheck';
import { FeatureSettingsCircle } from './FeatureSettingsCircle';
import { FeatureSettingsImage } from './FeatureSettingsImage';
import { FeatureSettingsPoint } from './FeatureSettingsPoint';
import { FeatureSettingsPolygon } from './FeatureSettingsPolygon';
import { FeatureSettingsPolyline } from './FeatureSettingsPolyline';
import { FeatureSettingsText } from './FeatureSettingsText';

interface Props {
  marker: STMapMarkerFeature;
  isNew: boolean;
}

const items: DropdownItem<FeatureType>[] = [
  { id: FeatureType.noFunc, name: '无功能' },
  { id: FeatureType.scene, name: '场景切换' },
  { id: FeatureType.link, name: '超链接' },
  { id: FeatureType.mobile, name: '电话' },
  { id: FeatureType.map, name: '导航' },
  { id: FeatureType.media, name: '图文' },
  { id: FeatureType.text, name: '文本' },
  { id: FeatureType.article, name: '文章' },
  { id: FeatureType.doc, name: 'PDF' },
  { id: FeatureType.audio, name: '音频' },
  { id: FeatureType.video, name: '视频' },
  { id: FeatureType.miniProgram, name: '小程序' },
  { id: FeatureType.model3d, name: '3D模型' },
  { id: FeatureType.seqFrame, name: '逐帧环物' },
  { id: FeatureType.event, name: '事件' },
];

export function MarkerSettingsFeature({ marker, isNew }: Props) {
  const { type: markerType, data: feat } = marker;
  const [tabIndex, setTabIndex] = useState(0);

  const setFeat: CFSetData<CustomFeature> = (cb) => {
    $editingMapMarkerApi.update((draft) => {
      if (!isMapFeatureMarker(draft)) return;
      cb(draft.data);
    });
  };

  const handleFeatChange = (code: FeatureType) => {
    $editingMapMarkerApi.update((draft) => {
      if (!isMapFeatureMarker(draft)) return;
      draft.data = customButtonDefault(code);
    });
  };

  const renderContent = () => {
    switch (markerType) {
      case STMapMarkerType.point:
        return <FeatureSettingsPoint marker={marker} />;
      case STMapMarkerType.text:
        return <FeatureSettingsText marker={marker} />;
      case STMapMarkerType.image:
        return <FeatureSettingsImage marker={marker} />;
      case STMapMarkerType.polyline:
        return <FeatureSettingsPolyline marker={marker} isNew={isNew} />;
      case STMapMarkerType.circle:
        return <FeatureSettingsCircle marker={marker} isNew={isNew} />;
      case STMapMarkerType.polygon:
        return <FeatureSettingsPolygon marker={marker} isNew={isNew} />;
      default:
        return null;
    }
  };

  return (
    <>
      <div className="sticky top-0 z-10 bg-black pb-10">
        <Tabbar
          tabs={['样式', '交互']}
          activeIndex={tabIndex}
          onChange={setTabIndex}
        />
      </div>
      <div className={cn({ hidden: tabIndex !== 0 })}>{renderContent()}</div>
      <div className={cn({ hidden: tabIndex !== 1 })}>
        <CustomFeatureProvider setData={setFeat} data={feat}>
          <Collapse title="功能类型">
            <Dropdown
              items={items}
              selected={feat.code}
              onChange={handleFeatChange}
            />
          </Collapse>
          <FeatureSwitch code={feat.code} />
          {feat.code === FeatureType.scene && <CSceneSetting />}
        </CustomFeatureProvider>
      </div>
    </>
  );
}
